import { apiClient } from './apiClient';

export type MarketSegmentType =
  | 'Industry'
  | 'CompanySize'
  | 'Geography'
  | 'Vertical'
  | 'Custom';

export type MarketSegmentPriority = 'Primary' | 'Secondary' | 'Experimental';

export type MarketSegment = {
  id: string;
  name: string;
  description: string;
  segmentType: MarketSegmentType;
  metadataJson?: string | null;
  isActive: boolean;
  sortOrder: number;
  createdAtUtc: string;
  updatedAtUtc: string;
};

export type MarketSegmentListResult = {
  items: MarketSegment[];
  totalCount: number;
  page: number;
  pageSize: number;
};

export type MarketSegmentUpsertPayload = {
  name: string;
  description?: string;
  segmentType?: MarketSegmentType;
  metadataJson?: string;
  isActive?: boolean;
  sortOrder?: number;
};

export type SalesProductMarketSegmentLink = {
  marketSegmentId: string;
  name: string;
  segmentType: MarketSegmentType;
  priority: MarketSegmentPriority;
  sortOrder: number;
};

export type SalesProductMarketSegmentLinkItemPayload = {
  marketSegmentId: string;
  priority?: MarketSegmentPriority;
  sortOrder: number;
};

export type MarketSegmentListFilter = {
  activeOnly?: boolean;
  page?: number;
  pageSize?: number;
  search?: string;
};

export async function listMarketSegments(
  filter: MarketSegmentListFilter = {},
): Promise<MarketSegmentListResult> {
  const response = await apiClient.get<MarketSegmentListResult>('/v1/sales/market-segments', {
    params: {
      activeOnly: filter.activeOnly ?? false,
      page: filter.page ?? 1,
      pageSize: filter.pageSize ?? 25,
      search: filter.search || undefined,
    },
  });
  return response.data;
}

export async function createMarketSegment(payload: MarketSegmentUpsertPayload): Promise<MarketSegment> {
  const response = await apiClient.post<MarketSegment>('/v1/sales/market-segments', payload);
  return response.data;
}

export async function updateMarketSegment(
  id: string,
  payload: MarketSegmentUpsertPayload,
): Promise<MarketSegment> {
  const response = await apiClient.put<MarketSegment>(`/v1/sales/market-segments/${id}`, payload);
  return response.data;
}

export async function deleteMarketSegment(id: string): Promise<void> {
  await apiClient.delete(`/v1/sales/market-segments/${id}`);
}

export type MarketSegmentImportQueued = {
  importId: string;
  hangfireJobId?: string | null;
  status: string;
  message: string;
};

export type MarketSegmentImportStatus = {
  id: string;
  originalFileName: string;
  status: string;
  hangfireJobId?: string | null;
  totalRows: number;
  createdCount: number;
  updatedCount: number;
  skippedCount: number;
  failedCount: number;
  errorSummary?: string | null;
  createdAtUtc: string;
  startedAtUtc?: string | null;
  completedAtUtc?: string | null;
};

export async function downloadMarketSegmentImportTemplate(): Promise<Blob> {
  const response = await apiClient.get('/v1/sales/market-segments/import/template', {
    responseType: 'blob',
  });
  return response.data;
}

export async function importMarketSegments(file: File): Promise<MarketSegmentImportQueued> {
  const formData = new FormData();
  formData.append('file', file);
  const { data } = await apiClient.post<MarketSegmentImportQueued>(
    '/v1/sales/market-segments/import',
    formData,
    { headers: { 'Content-Type': 'multipart/form-data' } },
  );
  return data;
}

export async function getMarketSegmentImportStatus(
  importId: string,
): Promise<MarketSegmentImportStatus> {
  const { data } = await apiClient.get<MarketSegmentImportStatus>(
    `/v1/sales/market-segments/import/${importId}`,
  );
  return data;
}

export async function getProductMarketSegments(productId: string): Promise<SalesProductMarketSegmentLink[]> {
  const response = await apiClient.get<SalesProductMarketSegmentLink[]>(
    `/v1/sales/products/${productId}/market-segments`,
  );
  return response.data;
}

export async function replaceProductMarketSegments(
  productId: string,
  items: SalesProductMarketSegmentLinkItemPayload[],
): Promise<void> {
  await apiClient.put(`/v1/sales/products/${productId}/market-segments`, { items });
}
